import { useState, useEffect } from "react";
import { getEmotionalColor, getEmotionalLabel } from "./normalize";

const ZONES = [
  { min: 0, max: 25, color: "#C0453A", label: "Crítico" },
  { min: 26, max: 45, color: "#D4756A", label: "Bajo" },
  { min: 46, max: 65, color: "#F9A01B", label: "Moderado" },
  { min: 66, max: 80, color: "#157346", label: "Bueno" },
  { min: 81, max: 100, color: "#0E8C3A", label: "Excelente" },
];

export function EmotionalThermo({ score, animate }) {
  const [level, setLevel] = useState(0);
  const target = Math.max(0, Math.min(100, score));

  useEffect(() => {
    if (animate) {
      const t = setTimeout(() => setLevel(target), 80);
      return () => clearTimeout(t);
    } else {
      setLevel(0);
    }
  }, [animate, target]);

  const color = getEmotionalColor(score);
  const label = getEmotionalLabel(score);

  const barH = 160, barW = 32, barX = 16, barY = 10;
  const fillH = (level / 100) * barH;
  const markerY = barY + barH - (level / 100) * barH;

  return (
    <div className="flex items-center justify-center gap-5" data-testid="emotional-thermo">
      <svg viewBox="0 0 80 185" style={{ width: 60, height: 185 }}>
        {/* Background bar with zones */}
        <defs>
          <clipPath id="thermoClip">
            <rect x={barX} y={barY} width={barW} height={barH} rx="16" />
          </clipPath>
        </defs>

        {/* Zone colors (bottom to top) */}
        <g clipPath="url(#thermoClip)">
          {ZONES.map((z, i) => {
            const zoneBottom = barY + barH - (z.max / 100) * barH;
            const zoneH = ((z.max - z.min + 1) / 100) * barH;
            return (
              <rect key={i} x={barX} y={zoneBottom} width={barW} height={zoneH}
                fill={z.color} opacity={0.2} />
            );
          })}
          {/* Fill level */}
          <rect x={barX} y={barY + barH - fillH} width={barW} height={fillH}
            fill={color} opacity="0.7"
            style={{ transition: "all 1.2s cubic-bezier(0.4, 0, 0.2, 1)" }} />
        </g>

        {/* Bar outline */}
        <rect x={barX} y={barY} width={barW} height={barH} rx="16"
          fill="none" stroke="#E2D9CB" strokeWidth="1.5" />

        {/* Marker line */}
        <line x1={barX - 4} y1={markerY} x2={barX + barW + 4} y2={markerY}
          stroke={color} strokeWidth="2.5" strokeLinecap="round"
          style={{ transition: "all 1.2s cubic-bezier(0.4, 0, 0.2, 1)" }} />
        <circle cx={barX + barW + 8} cy={markerY} r="4" fill={color}
          style={{ transition: "all 1.2s cubic-bezier(0.4, 0, 0.2, 1)" }} />

        {/* Zone labels on the left */}
        <text x={barX - 6} y={barY + barH - 5} textAnchor="end" fontSize="7" fill="#B5A998">0</text>
        <text x={barX - 6} y={barY + barH / 2 + 2} textAnchor="end" fontSize="7" fill="#B5A998">50</text>
        <text x={barX - 6} y={barY + 5} textAnchor="end" fontSize="7" fill="#B5A998">100</text>
      </svg>

      {/* Score display */}
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
          <span style={{ fontSize: 28, fontWeight: 800, color: "#2D2A24", lineHeight: 1 }}>
            {score}
          </span>
          <span style={{ fontSize: 14, color: "#71644C" }}>/100</span>
        </div>
        <span className="inline-block mt-2 text-xs font-bold px-2.5 py-0.5 rounded-lg"
          style={{ background: `${color}15`, color }}>
          {label}
        </span>
      </div>
    </div>
  );
}
